import { Box, Grid, GridItem, HStack, Text, VStack } from '@chakra-ui/react';
import React, { FC, useMemo } from 'react';
import { useKpNftPositionDetails } from '@app/screens/positions/components/SpNftPositionListRowItem/hooks';
import { useFarmBaseApr } from '@app/hooks/plugins/useFarmBaseApr';
import { useMarketMakerApr } from '@app/hooks/merkl/useMarketMakerApr';
import { useKpNftLockDetails } from '@app/hooks/plugins/useKpNftLockDetails';
import { PairToken } from '@app/types/pool';
import { LogoSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/LogoSection';
import { SymbolsSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/SymbolsSection';
import { APRSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/APRSection';
import { PropertiesSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/PropertiesSection';
import { LockedUntilSection } from '@app/screens/nft-marketplace/LockedUntilSection';
import { DepositSection } from '@app/screens/nft-marketplace/DepositSection';
import { BuyNowButton } from '@app/screens/nft-marketplace/BuyNowButton';
import { SellPrice } from '@app/screens/nft-marketplace/SellPrice';
import styles from '@app/screens/nft-marketplace/SpNftMarketPlaceList/SpNftMarketPlaceList.module.scss';
import { StrategySection } from '@app/components/StrategySection';
import { useYieldBoosterApr } from '@app/hooks/booster/useYieldBoosterApr';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import { APP_ROUTES } from '@app/constants/routes';
import Link from 'next/link';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { getTypeAcronym } from '@app/screens/nft-marketplace/SpNftMarketPlaceList/SprNftMarketplaceCell/helpers';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';
import { useConfig } from '@app/config';
import { BidNowButton } from '@app/screens/nft-marketplace/BidNowButton';
import { CancelListingButton } from '@app/screens/nft-marketplace/CancelListingButton';
import clsx from 'clsx';
import { PremiumNftBadge } from '@app/screens/nft-marketplace/PremiumNftBadge';
import { SettleListingButton } from '@app/screens/nft-marketplace/SettleListingButton';
import { DiscountCell } from '@app/screens/nft-marketplace/DiscountCell';

type Props = {
  isMobile: boolean;
  poolAddress: string;
  tokenId: string;
  tokenDetails: PairToken | undefined;
  isStakedToNitro?: boolean;
  nitroPoolId?: string;
  data: NFTMarketplaceListing;
};

export const SprNftMarketplaceRowItem: FC<Props> = ({
  isMobile,
  poolAddress,
  tokenId,
  tokenDetails,
  isStakedToNitro,
  data: listing
}) => {
  const { vault: vaultFilter } = useNftMarketplaceFiltersState();
  const { vault, usdAmount, pendingRewards, data } = useKpNftPositionDetails(
    tokenId,
    poolAddress,
    tokenDetails?.id ?? ''
  );

  const { totalApr, lockBonusApr, farmBaseApr } = useFarmBaseApr(
    poolAddress,
    vault?.tvl,
    vault?.apr,
    (data?.lockDuration ?? 0) / 1000 / 3600 / 24
  );

  const bonusApr = useYieldBoosterApr({ poolAddress, tokenId, farmBaseApr });

  const marketMakerAPR = useMarketMakerApr(
    vault?.token0.id,
    vault?.token1.id,
    vault?.lpToken.id
  );

  const lockDetails = useKpNftLockDetails(data);

  const config = useConfig();
  const TOKENS_WHITELIST = getTokensWhitelist(config);

  const sellCurrency = useMemo(() => {
    return TOKENS_WHITELIST.find(
      item =>
        item.contractAddress.toLowerCase() === listing.currency.toLowerCase()
    );
  }, [listing, TOKENS_WHITELIST]);

  // filters
  // if (
  //   searchTerm &&
  //   !vault?.token0.symbol.toLowerCase().includes(searchTerm.toLowerCase()) &&
  //   !vault?.token1.symbol.toLowerCase().includes(searchTerm.toLowerCase()) &&
  //   !`${vault?.token0.symbol}-${vault?.token1.symbol}`
  //     .toLowerCase()
  //     .includes(searchTerm.toLowerCase())
  // ) {
  //   return null;
  // }

  if (vaultFilter && vault?.provider?.toLowerCase() !== vaultFilter) {
    return null;
  }

  if (!vault || !sellCurrency) {
    return null;
  }

  return (
    <Link href={APP_ROUTES.nftDetailsPage(listing.id)}>
      {isMobile ? (
        <VStack
          gap={3}
          width="100%"
          className={clsx(styles.nftItemMobile, {
            [styles.premium]: listing.nftMetadata.isPremium
          })}
        >
          <HStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <HStack>
              {listing.nftMetadata.isPremium ? (
                <PremiumNftBadge />
              ) : (
                <Box width="24px" />
              )}
              <LogoSection vault={vault} tokenDetails={tokenDetails} />
              <VStack alignItems="flex-start" gap={1}>
                <SymbolsSection vault={vault} tokenDetails={tokenDetails} />
                <Text fontSize="14px" color="gray.4">
                  ID: {tokenId}
                </Text>
              </VStack>
            </HStack>
            <PropertiesSection
              lockDetails={lockDetails}
              poolAddress={poolAddress}
              tokenId={tokenId}
              isStakedToNitro={isStakedToNitro}
            />
            <CancelListingButton
              listing={listing}
              sellCurrency={sellCurrency}
            />
            <SettleListingButton
              listing={listing}
              sellCurrency={sellCurrency}
              variant="rowCompact"
            />
          </HStack>
          <HStack
            alignItems="flex-start"
            justifyContent="space-between"
            width="100%"
          >
            <Text fontSize="14px" color="gray.4">
              Vault
            </Text>
            <StrategySection vault={vault} />
          </HStack>
          <HStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Text fontSize="14px" color="gray.4">
              APR
            </Text>
            <APRSection
              totalApr={totalApr}
              farmBaseApr={farmBaseApr}
              swapFeesApr={vault?.apr}
              lockBonusApr={lockBonusApr}
              marketMakerAPR={marketMakerAPR}
              yieldBonusApr={bonusApr}
            />
          </HStack>
          <HStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Text fontSize="14px" color="gray.4">
              Locked Until
            </Text>
            <LockedUntilSection date={lockDetails?.lockedUntilDate} />
          </HStack>
          <HStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Text fontSize="14px" color="gray.4">
              Auction Type
            </Text>
            <Text fontSize="14px" lineHeight="26px" color="gray.4">
              {getTypeAcronym(listing.type)}
            </Text>
          </HStack>
          <HStack
            alignItems="flex-start"
            justifyContent="space-between"
            width="100%"
          >
            <Text fontSize="14px" color="gray.4">
              Deposits
            </Text>
            <DepositSection
              amount={data ? data.amount : 0}
              usdAmount={usdAmount}
            />
          </HStack>
          <HStack
            alignItems="flex-start"
            justifyContent="space-between"
            width="100%"
          >
            <Text fontSize="14px" color="gray.4">
              Sell price
            </Text>
            <SellPrice currency={sellCurrency} listing={listing} />
          </HStack>
          {listing.type === 'BUY_IT_NOW' ||
          listing.type === 'BUY_IT_NOW_WITH_BIDS' ? (
            <BuyNowButton
              classes={{
                root: styles.buyButtonRoot,
                buyButton: styles.buyButton,
                removeFromCart: styles.removeFromCart
              }}
              listing={listing}
            />
          ) : (
            <BidNowButton
              classes={{ root: styles.bidButtonRoot }}
              sellCurrency={sellCurrency}
              listing={listing}
            />
          )}
        </VStack>
      ) : (
        <Grid
          className={clsx(styles.tableRow, {
            [styles.premium]: listing.nftMetadata.isPremium
          })}
        >
          <GridItem display="flex" alignItems="center">
            <HStack>
              {listing.nftMetadata.isPremium ? (
                <PremiumNftBadge />
              ) : (
                <Box width="24px" />
              )}
              <LogoSection vault={vault} tokenDetails={tokenDetails} />
              <VStack alignItems="flex-start" gap={1}>
                <SymbolsSection vault={vault} tokenDetails={tokenDetails} />
                <Text fontSize="14px" color="gray.4">
                  ID: {tokenId}
                </Text>
              </VStack>
            </HStack>
          </GridItem>
          <GridItem display="flex" alignItems="flex-start">
            <StrategySection vault={vault} />
          </GridItem>
          <GridItem>
            <APRSection
              totalApr={totalApr}
              farmBaseApr={farmBaseApr}
              swapFeesApr={vault?.apr}
              lockBonusApr={lockBonusApr}
              marketMakerAPR={marketMakerAPR}
              yieldBonusApr={bonusApr}
            />
          </GridItem>

          <GridItem>
            <PropertiesSection
              lockDetails={lockDetails}
              poolAddress={poolAddress}
              tokenId={tokenId}
              isStakedToNitro={isStakedToNitro}
            />
          </GridItem>
          <GridItem>
            <LockedUntilSection date={lockDetails?.lockedUntilDate} />
          </GridItem>
          <GridItem>
            <Text fontSize="14px" lineHeight="26px" color="gray.4">
              {getTypeAcronym(listing.type)}
            </Text>
          </GridItem>
          <GridItem>
            <DepositSection
              amount={data ? data.amount : 0}
              usdAmount={usdAmount}
            />
          </GridItem>
          <GridItem>
            <SellPrice currency={sellCurrency} listing={listing} />
          </GridItem>
          <GridItem>
            <DiscountCell
              currency={sellCurrency}
              listing={listing}
              deposit={usdAmount}
            />
          </GridItem>
          <GridItem>
            <CancelListingButton
              listing={listing}
              sellCurrency={sellCurrency}
            />
            <SettleListingButton
              listing={listing}
              sellCurrency={sellCurrency}
              variant="rowCompact"
            />
            {listing.type === 'BUY_IT_NOW' ||
            listing.type === 'BUY_IT_NOW_WITH_BIDS' ? (
              <BuyNowButton
                classes={{
                  root: styles.buyButtonRoot,
                  removeFromCart: styles.removeFromCart
                }}
                listing={listing}
              />
            ) : (
              <BidNowButton
                classes={{ root: styles.bidButtonRoot }}
                sellCurrency={sellCurrency}
                listing={listing}
              />
            )}
          </GridItem>
        </Grid>
      )}
    </Link>
  );
};
